import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Server, CheckCircle, XCircle } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "The Castle Analogy",
        icon: "🏰",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <h3 className="font-bold text-xl text-blue-800 mb-3">One Wall isn't Enough</h3>
                    <p className="text-slate-700 mb-4">
                        Imagine a castle with just one wooden door. If an attacker breaks that door, they get the treasure!
                    </p>
                    <p className="text-slate-700">
                        <strong>Defense in Depth</strong> means having multiple layers of security. If one fails, the next one stops the intruder.
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The Layers",
        icon: "🧅",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 text-lg">A strong digital fortress has 4 main layers:</p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl">🧱</span>
                            <span className="font-bold text-green-800">1. Perimeter</span>
                        </div>
                        <p className="text-sm text-slate-600">Firewalls block bad traffic before it even enters.</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl">🆔</span>
                            <span className="font-bold text-yellow-800">2. Identity</span>
                        </div>
                        <p className="text-sm text-slate-600">MFA ensures only the right people get in.</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl">💻</span>
                            <span className="font-bold text-blue-800">3. Endpoint</span>
                        </div>
                        <p className="text-sm text-slate-600">Antivirus protects the actual laptops/servers.</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl">💎</span>
                            <span className="font-bold text-purple-800">4. Data</span>
                        </div>
                        <p className="text-sm text-slate-600">Encryption protects the treasure itself!</p>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Why Redundancy Matters",
        icon: "🛡️",
        content: (
            <div className="space-y-6">
                <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-200">
                    <h3 className="font-bold text-xl text-red-800 mb-3">Attackers are Clever</h3>
                    <p className="text-slate-700 mb-4">
                        Attackers WILL find a way past one layer. Maybe they steal a password (Identity layer breached).
                    </p>
                    <p className="text-slate-700">
                        But if the data is Encrypted (Data layer), the stolen password doesn't let them read the files!
                    </p>
                </div>
                <div className="bg-slate-100 p-4 rounded-xl border border-slate-200">
                    <h4 className="font-bold text-slate-800 mb-2">🎮 Mission:</h4>
                    <p className="text-slate-600 text-sm">
                        Build a fortress with enough layers to stop a simulated attack!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
const CastleGame = ({ onComplete }: { onComplete: () => void }) => {
    // Layer state: true = active/secure, false = inactive/vulnerable
    const [layers, setLayers] = useState({
        perimeter: false, // Firewall
        identity: false,  // MFA/Auth
        endpoint: false,  // Antivirus
        data: false       // Encryption
    });

    // Attack simulation state
    const [attackStatus, setAttackStatus] = useState<'idle' | 'attacking' | 'breached' | 'secured'>('idle');
    const [attackStep, setAttackStep] = useState(0); // 0 to 4 (penetration depth)

    const toggleLayer = (layer: keyof typeof layers) => {
        if (attackStatus === 'attacking') return; // Lock changes during attack
        setLayers(prev => ({ ...prev, [layer]: !prev[layer] }));
    };

    const runSimulation = () => {
        setAttackStatus('attacking');
        setAttackStep(0);

        // Simulate attack progression
        let currentStep = 0;
        const interval = setInterval(() => {
            currentStep++;
            setAttackStep(currentStep);

            // Check if current layer stops the attack
            let blocked = false;

            if (currentStep === 1) blocked = layers.perimeter;
            else if (currentStep === 2) blocked = layers.identity;
            else if (currentStep === 3) blocked = layers.endpoint;
            else if (currentStep === 4) blocked = layers.data;

            if (blocked) {
                clearInterval(interval);
                setAttackStatus('secured');

                // If all layers active (or critical combination), win
                const activeCount = Object.values(layers).filter(Boolean).length;
                if (activeCount >= 3) {
                    onComplete();
                }
            } else if (currentStep >= 4) {
                clearInterval(interval);
                setAttackStatus('breached');
            }

        }, 1000);
    };

    const reset = () => {
        setAttackStatus('idle');
        setAttackStep(0);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 text-slate-100">
            {/* The Castle Diagram */}
            <div className="relative h-[450px] bg-slate-800 rounded-3xl overflow-hidden border border-slate-700">
                {/* Core (Data) */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 text-center">
                    <motion.div
                        animate={{ scale: attackStatus === 'breached' ? [1, 1.2, 1] : 1 }}
                        className={`w-24 h-24 rounded-full flex items-center justify-center shadow-2xl transition-all duration-500 ${attackStatus === 'breached' ? 'bg-red-500 shadow-red-500/50' :
                            attackStatus === 'secured' ? 'bg-green-500 shadow-green-500/50' :
                                'bg-slate-700 border-4 border-slate-600'
                            }`}
                    >
                        <Server size={32} className="text-white" />
                    </motion.div>
                    <div className="mt-2 font-bold text-sm text-slate-400">DATA (Core)</div>
                </div>

                {/* Layers (Concentric Circles) */}
                {[
                    { id: 'data', radius: 160, label: 'Encryption', color: 'border-purple-500' },
                    { id: 'endpoint', radius: 240, label: 'Antivirus', color: 'border-blue-500' },
                    { id: 'identity', radius: 320, label: 'MFA Auth', color: 'border-yellow-500' },
                    { id: 'perimeter', radius: 400, label: 'Firewall', color: 'border-green-500' },
                ].map((layer, index) => {
                    const isActive = layers[layer.id as keyof typeof layers];
                    // Inverse index for attack step matching (Perimeter is step 1, but outer-most layer)
                    // Actually step 1 hits perimeter (largest radius).
                    const isHit = attackStep >= (4 - index);

                    return (
                        <div
                            key={layer.id}
                            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 transition-all duration-300 flex items-start justify-center pt-2 ${isActive ? layer.color + ' bg-opacity-10 bg-white' : 'border-slate-800 border-dashed opacity-30'
                                }`}
                            style={{ width: layer.radius, height: layer.radius, zIndex: 4 - index }}
                        >
                            <span className={`text-xs font-bold uppercase tracking-wider bg-slate-900 px-2 ${isActive ? 'text-white' : 'text-slate-600'}`}>
                                {layer.label}
                            </span>
                        </div>
                    );
                })}

                {/* Attack Blip */}
                {attackStatus === 'attacking' && (
                    <motion.div
                        className="absolute top-1/2 left-1/2 w-4 h-4 bg-red-500 rounded-full z-50 shadow-lg shadow-red-500"
                        initial={{ x: 250, y: 0 }} // Start outside
                        animate={{ x: 200 - (attackStep * 50) }} // Move inward (approx)
                        transition={{ duration: 0.5 }}
                    />
                )}
            </div>

            {/* Controls */}
            <div className="space-y-6 flex flex-col justify-center">
                <div className="bg-slate-800 rounded-3xl p-6 border border-slate-700 shadow-xl">
                    <h3 className="font-bold text-slate-300 mb-4">Security Configuration</h3>
                    <div className="space-y-3">
                        {[
                            { id: 'perimeter', label: '1. Perimeter Firewall', desc: 'Blocks network traffic' },
                            { id: 'identity', label: '2. Identity Access (MFA)', desc: 'Verifies user is real' },
                            { id: 'endpoint', label: '3. Device Security', desc: 'Stops malware on laptops' },
                            { id: 'data', label: '4. Data Encryption', desc: 'Scrambles data at rest' },
                        ].map(l => (
                            <button
                                key={l.id}
                                onClick={() => toggleLayer(l.id as any)}
                                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${layers[l.id as keyof typeof layers]
                                    ? 'bg-blue-900/30 border-blue-500 text-blue-200'
                                    : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-600'
                                    }`}
                            >
                                <div className="flex justify-between items-center">
                                    <span className="font-bold">{l.label}</span>
                                    {layers[l.id as keyof typeof layers] ? <CheckCircle size={18} /> : <XCircle size={18} />}
                                </div>
                                <div className="text-xs opacity-70 mt-1">{l.desc}</div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-3">
                    <button
                        onClick={runSimulation}
                        disabled={attackStatus === 'attacking'}
                        className={`w-full py-4 rounded-xl font-black text-lg shadow-lg transform transition-all active:scale-95 ${attackStatus === 'attacking' ? 'bg-slate-700 text-slate-500 cursor-not-allowed' :
                            'bg-red-500 hover:bg-red-600 text-white shadow-red-500/30'
                            }`}
                    >
                        {attackStatus === 'attacking' ? 'ATTACK IN PROGRESS ⚠️' : '🚨 SIMULATE ATTACK'}
                    </button>
                    {attackStatus !== 'idle' && attackStatus !== 'attacking' && (
                        <button onClick={reset} className="text-slate-400 hover:text-white underline text-sm text-center w-full">
                            Reset Simulation
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const DefenseInDepth = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('defense-in-depth', 100);
    };

    return (
        <LessonWrapper
            moduleName="Defense in Depth"
            moduleEmoji="🛡️"
            moduleColor="blue"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Activity"
        >
            <CastleGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default DefenseInDepth;
